import { uniq } from "lodash";
import { 
    APPLICATION_ROLE, APPLICATION_ROLE_GRAD_FACULTY_PREFIX, APPLICATION_ROLE_GRAD_PLAN_ADMIN_PREFIX, APPLICATION_ROLE_GRAD_PLAN_ASSOCIATE_PREFIX,
    APPLICATION_ROLE_GRADUATE_APPROVER_PREFIX, DEGREE, EXTERNAL_ROLE, PROFESSIONAL_DEGREES
} from "../util/constants";
import { getProgram } from "../util/functions";

function transformExternalUser(user = {}){
    const { externalUserId, emailAddr, firstName, lastName, roleName, externalKeys } = user;
    return Object.freeze({
        externalUserId,
        email: emailAddr,
        fullName: `${firstName} ${lastName}`,
        name: `${firstName} ${lastName}`,
        initials: `${firstName && firstName.charAt(0)}${lastName && lastName.charAt(0)}`.toUpperCase(),
        applicationRoles: [roleName],
        isAuthorized: roleName === EXTERNAL_ROLE,
        isExternal: roleName === EXTERNAL_ROLE,
        externalKeys
    });
}

function transformUser(userDetails, proxy = {}) {
    const academicPlan = userDetails.academicPlan;
    const program = getProgram(academicPlan);

    const applicationRoles = Object.freeze((userDetails?.applicationRoles && userDetails.applicationRoles.length > 0) ? userDetails.applicationRoles.split(",") : []);

    const facultyRoles = Object.freeze(applicationRoles.filter(applicationRole => applicationRole.startsWith(APPLICATION_ROLE_GRAD_FACULTY_PREFIX)));
    const facultyPrograms = Object.freeze(facultyRoles.map(facultyRole => (facultyRole.replace(APPLICATION_ROLE_GRAD_FACULTY_PREFIX, ""))));

    const gradPlanAdminRoles = Object.freeze(applicationRoles.filter(applicationRole => applicationRole.startsWith(APPLICATION_ROLE_GRAD_PLAN_ADMIN_PREFIX)));
    const gradPlanAdminPlans = Object.freeze(gradPlanAdminRoles.map(gradPlanAdminRole => (gradPlanAdminRole.replace(APPLICATION_ROLE_GRAD_PLAN_ADMIN_PREFIX, ""))));
    const gradPlanAdminPrograms = Object.freeze(uniq(gradPlanAdminRoles.map(gradPlanAdminRole => getProgram(gradPlanAdminRole.replace(APPLICATION_ROLE_GRAD_PLAN_ADMIN_PREFIX, "")))));

    const gradPlanAssociateRoles = Object.freeze(applicationRoles.filter(applicationRole => applicationRole.startsWith(APPLICATION_ROLE_GRAD_PLAN_ASSOCIATE_PREFIX)));
    const gradPlanAssociatePlans = Object.freeze(gradPlanAssociateRoles.map(gradPlanAssociateRole => (gradPlanAssociateRole.replace(APPLICATION_ROLE_GRAD_PLAN_ASSOCIATE_PREFIX, ""))));
    const gradPlanAssociatePrograms = Object.freeze(uniq(gradPlanAssociatePlans.map(gradPlanAssociateRole => getProgram(gradPlanAssociateRole.replace(APPLICATION_ROLE_GRAD_PLAN_ASSOCIATE_PREFIX, "")))));

    const isGradStudent = applicationRoles.includes(APPLICATION_ROLE.GRAD_STUDENT);
    const isProfessionalStudent = applicationRoles.includes(APPLICATION_ROLE.PROFESSIONAL_STUDENT);
    const isUnderGradStudent = applicationRoles.includes(APPLICATION_ROLE.UNDERGRAD_STUDENT);

    const isEnrollmentAdmin = applicationRoles.includes(APPLICATION_ROLE.ENROLLMENT_ADMIN);
    const isGraduationAdmin = applicationRoles.includes(APPLICATION_ROLE.GRADUATION_ADMIN);

    const graduateApproverRoles = Object.freeze(applicationRoles.filter(applicationRole => applicationRole.startsWith(APPLICATION_ROLE_GRADUATE_APPROVER_PREFIX)));
    const graduateApproverPrograms = Object.freeze(graduateApproverRoles.map(graduateApproverRole => (graduateApproverRole.replace(APPLICATION_ROLE_GRADUATE_APPROVER_PREFIX, ""))));
    const isGraduateApprover = (graduateApproverRoles.length > 0);

    return Object.freeze({
        academicPlan,
        applicationRoles,
        careerNumber: userDetails.careerNumber,
        degree: userDetails.degree,
        email: userDetails.email,
        facultyPrograms,
        facultyRoles,
        fullName: userDetails.fullName,
        gradPlanAdminPlans,
        gradPlanAdminPrograms,
        gradPlanAssociatePlans,
        gradPlanAssociatePrograms,
        graduateApproverPrograms,
        graduateApproverRoles,
        initials: userDetails.initials,
        isAdmin: (isEnrollmentAdmin || isGraduationAdmin),
        isAuthorized: applicationRoles.length > 0,
        isContactApprover: applicationRoles.includes(APPLICATION_ROLE.CONTACT_APPROVER),
        isEnrollmentAdmin,
        isExternal: false,
        isFaculty: (facultyRoles.length > 0),
        isFacultyApprover: applicationRoles.includes(APPLICATION_ROLE.FACULTY_APPROVER),
        isGradPlanAdmin: (gradPlanAdminRoles.length > 0),
        isGradPlanAssociate: (gradPlanAdminRoles.length === 0 && (gradPlanAssociateRoles.length > 0)),
        isGradStudent,
        isGraduateApprover,
        isGraduationAdmin,
        isInquirer: applicationRoles.includes(APPLICATION_ROLE.INQUIRER),
        isProfessionalStudent,
        isProxy: Boolean(proxy?.osuid),
        isStudent: (isGradStudent || isProfessionalStudent || isUnderGradStudent),
        isUnderGradStudent,
        hasDoctoralAcademicPlan: [DEGREE.DMA, DEGREE.PHD].includes(userDetails.degree),
        hasProfessionalAcademicPlan: PROFESSIONAL_DEGREES.includes(userDetails.degree),
        name: userDetails.name,
        osuid: userDetails.osuid,
        pcdIndicator: userDetails.pcdIndicator,
        program,
        proxy
    });
}

export { transformUser, transformExternalUser }